import React, { useMemo } from 'react';
import StatCard from './StatCard';
import { FaTachometerAlt, FaChartLine, FaFlagCheckered, FaStopwatch } from 'react-icons/fa';
import './StatisticsPanel.css';

const StatisticsPanel = ({ lapData, driverName }) => {
  const statistics = useMemo(() => {
    if (!lapData || lapData.length === 0) return null;

    const lapDurations = lapData
      .map(lap => lap.lap_duration)
      .filter(duration => duration && duration > 0);

    if (lapDurations.length === 0) return null;

    const sector1Times = lapData
      .map(lap => lap.duration_sector_1)
      .filter(time => time && time > 0);
    const sector2Times = lapData
      .map(lap => lap.duration_sector_2)
      .filter(time => time && time > 0);
    const sector3Times = lapData
      .map(lap => lap.duration_sector_3)
      .filter(time => time && time > 0);

    const average = lapDurations.reduce((a, b) => a + b, 0) / lapDurations.length;
    const best = Math.min(...lapDurations);
    const worst = Math.max(...lapDurations);
    
    // Calculate standard deviation (consistency)
    const variance = lapDurations.reduce((acc, val) => acc + Math.pow(val - average, 2), 0) / lapDurations.length;
    const stdDev = Math.sqrt(variance);
    const consistency = ((1 - (stdDev / average)) * 100).toFixed(1);

    // Best sector times
    const bestSector1 = sector1Times.length > 0 ? Math.min(...sector1Times) : 0;
    const bestSector2 = sector2Times.length > 0 ? Math.min(...sector2Times) : 0;
    const bestSector3 = sector3Times.length > 0 ? Math.min(...sector3Times) : 0;

    // Calculate percentile (simplified - assuming normal distribution)
    const sortedLaps = [...lapDurations].sort((a, b) => a - b);
    const median = sortedLaps[Math.floor(sortedLaps.length / 2)];
    const percentile = average <= median ? 'Top 50%' : 'Bottom 50%';

    return {
      average: average.toFixed(3),
      best: best.toFixed(3),
      worst: worst.toFixed(3),
      consistency: `${consistency}%`,
      bestSector1: bestSector1.toFixed(3),
      bestSector2: bestSector2.toFixed(3),
      bestSector3: bestSector3.toFixed(3),
      stdDev: stdDev.toFixed(3),
      percentile,
      lapCount: lapDurations.length,
    };
  }, [lapData]);

  if (!statistics) {
    return (
      <div className="statistics-panel">
        <h3>Statistics</h3>
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="statistics-panel">
      <h3 className="statistics-title">{driverName} - Statistics</h3>
      <div className="statistics-grid">
        <StatCard
          title="Average Lap"
          value={statistics.average}
          unit="s"
          icon={<FaTachometerAlt />}
        />
        <StatCard
          title="Best Lap"
          value={statistics.best}
          unit="s"
          icon={<FaFlagCheckered />}
        />
        <StatCard
          title="Worst Lap"
          value={statistics.worst}
          unit="s"
          icon={<FaStopwatch />}
        />
        <StatCard
          title="Consistency"
          value={statistics.consistency}
          icon={<FaChartLine />}
          trend={{
            type: parseFloat(statistics.consistency) > 95 ? 'positive' : 'neutral',
            text: parseFloat(statistics.consistency) > 95 ? 'Very Consistent' : 'Moderate',
          }}
        />
        <StatCard
          title="Std Deviation"
          value={statistics.stdDev}
          unit="s"
        />
        <StatCard
          title="Laps Analyzed"
          value={statistics.lapCount}
        />
      </div>
      <div className="best-sectors">
        <h4>Best Sector Times</h4>
        <div className="sector-times-grid">
          <div className="sector-time-item">
            <span className="sector-label">Sector 1</span>
            <span className="sector-value">{statistics.bestSector1}s</span>
          </div>
          <div className="sector-time-item">
            <span className="sector-label">Sector 2</span>
            <span className="sector-value">{statistics.bestSector2}s</span>
          </div>
          <div className="sector-time-item">
            <span className="sector-label">Sector 3</span>
            <span className="sector-value">{statistics.bestSector3}s</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;

